import { useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';

import { api, ErroApi } from '../api';
import { Aviso, BotaoTema, Campo, Logotipo } from '../comum';
import type { Capacidades, Usuario } from '../tipos';

interface RespostaLogin {
  readonly usuario: Usuario;
  readonly capacidades: Capacidades;
  readonly appliance: { readonly versao: string | null };
}

/**
 * Último usuário, para não redigitar todo dia.
 *
 * 🔴 **Guardamos o usuário e a organização. Nunca a senha.**
 *
 * Guardar senha em `localStorage` a deixaria legível por qualquer script da
 * página, para sempre, em texto puro — e num produto que administra o parque
 * inteiro do cliente essa é a credencial errada para deixar cair. O navegador
 * já faz isso direito, com o cofre do sistema operacional, e é por isso que os
 * campos têm `autoComplete`: o gerenciador de senhas assume a parte sensível.
 */
const CHAVE_ULTIMO = 'lenanzo.ultimoAcesso';

function lerUltimoAcesso(): { usuario: string; organizacao: string } | null {
  try {
    const bruto = localStorage.getItem(CHAVE_ULTIMO);
    if (bruto === null) return null;
    const o = JSON.parse(bruto) as Record<string, unknown>;
    return {
      usuario: typeof o['usuario'] === 'string' ? o['usuario'] : '',
      organizacao: typeof o['organizacao'] === 'string' ? o['organizacao'] : '',
    };
  } catch {
    return null;
  }
}

/**
 * Entrada no produto.
 *
 * Não existe cadastro de usuários aqui: a credencial é a do KACE, validada
 * contra o próprio appliance. É o que faz "quem decidiu" ser um usuário
 * verificado e não um campo de texto que a pessoa preenche — e é por isso que a
 * tela explica isso em vez de só pedir usuário e senha.
 */
export function Login({
  organizacaoPadrao,
  organizacoes,
  nomeDoCliente,
  avisoInicial,
  acessoLocalDisponivel = false,
  aoEntrar,
}: {
  organizacaoPadrao: string;
  organizacoes: readonly string[];
  nomeDoCliente: string;
  avisoInicial: string | null;
  /** Só verdadeiro quando o servidor subiu com `ACESSO_LOCAL` no ambiente. */
  acessoLocalDisponivel?: boolean;
  aoEntrar: (u: Usuario, c: Capacidades) => void;
}): ReactNode {
  const [segredoLocal, setSegredoLocal] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [dica, setDica] = useState<string | null>(null);
  /**
   * 🔴 O aviso de que a PRÓXIMA tentativa pode desativar a conta no KACE.
   *
   * O servidor calcula e envia em `avisoBloqueio` (`rotas/auth.ts:256`) quando
   * o orçamento de tentativas está no fim. A tela lia `dica` e **descartava
   * este** — o aviso mais caro que o produto tem chegava no corpo da resposta e
   * morria ali.
   *
   * ⚠️ Medido em 2026-09-14: quatro tentativas seguidas, nenhum aviso na tela,
   * num appliance que desativa a conta com 3 falhas em 5 minutos. Depois disso
   * a senha CERTA volta `credencial_invalida` igualzinho à errada, e não há
   * como distinguir do lado de cá.
   */
  const [avisoBloqueio, setAvisoBloqueio] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [verSenha, setVerSenha] = useState(false);
  const ultimo = lerUltimoAcesso();
  const [lembrar, setLembrar] = useState(ultimo !== null);

  /**
   * 🔴 Campos NÃO controlados, lidos do DOM no envio.
   *
   * Bug real, encontrado na primeira tentativa de login de verdade: o
   * preenchimento automático do navegador (e o do gerenciador de senhas)
   * escreve direto no DOM **sem disparar `onChange`**. Com campos controlados
   * por estado, o React continua achando que estão vazios — a tela mostra a
   * senha preenchida, o servidor recebe string vazia, e o usuário lê "Informe
   * usuário e senha" olhando para os campos preenchidos.
   *
   * `FormData` lê o que está de fato no formulário, venha de onde vier.
   */
  async function enviar(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const form = e.currentTarget;
    const dados = new FormData(form);
    const usuario = String(dados.get('usuario') ?? '').trim();
    // A senha NÃO é aparada: espaço nas bordas pode ser parte dela, e aparar em
    // silêncio faria a senha certa falhar aqui e funcionar no console do KACE.
    const senha = String(dados.get('senha') ?? '');
    const organizacao = String(dados.get('organizacao') ?? '').trim();

    setEntrando(true);
    // O erro anterior sai já: deixá-lo ao lado de "Entrando…" faz parecer que a
    // nova tentativa já falhou.
    setErro(null);
    setDica(null);

    try {
      const r = await api.post<RespostaLogin>('/api/auth/login', {
        usuario,
        senha,
        organizacao,
      });

      // Só depois do login dar certo: guardar um usuário que nem existe faria a
      // próxima visita começar com um campo errado preenchido.
      if (lembrar) {
        localStorage.setItem(CHAVE_ULTIMO, JSON.stringify({ usuario, organizacao }));
      } else {
        localStorage.removeItem(CHAVE_ULTIMO);
      }

      form.reset();
      aoEntrar(r.usuario, r.capacidades);
    } catch (e2: unknown) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
      if (e2 instanceof ErroApi && typeof e2.corpo['dica'] === 'string') {
        setDica(e2.corpo['dica']);
      }
      if (e2 instanceof ErroApi && typeof e2.corpo['avisoBloqueio'] === 'string') {
        setAvisoBloqueio(e2.corpo['avisoBloqueio']);
      }
    } finally {
      setEntrando(false);
    }
  }

  /**
   * Caps Lock ligado é a causa silenciosa de "senha inválida" mais comum que
   * existe — e a única que o usuário não consegue ver, porque o campo mostra
   * pontos. Detectamos no evento, sem ler o conteúdo digitado.
   */
  const conferirCapsLock = (e: KeyboardEvent<HTMLInputElement>): void => {
    setCapsLock(e.getModifierState('CapsLock'));
  };

  return (
    <div className="entrada">
      {/* Painel de marca — some em telas estreitas, onde o formulário é tudo. */}
      <aside className="entrada-marca">
        <div>
          <Logotipo tamanho={40} descritivo invertido />
          <h1>Plataforma KACE</h1>
          <ul>
            <li>Inventário, chamados e conformidade em uma tela só</li>
            <li>Inteligência artificial rodando dentro do seu ambiente</li>
            <li>A IA propõe, você autoriza — nada é executado sozinho</li>
          </ul>
        </div>
        {nomeDoCliente !== '' && <p className="cliente">Instalação de {nomeDoCliente}</p>}
      </aside>

      <main className="entrada-form">
        <div className="canto-tema">
          <BotaoTema />
        </div>
        <div className="caixa">
          <div className="so-estreito">
            <Logotipo tamanho={26} />
          </div>
          <h2>Entrar</h2>
          <p className="sub">
            Use a sua credencial do <strong>KACE</strong>. As permissões são as mesmas
            que você já tem no console — o produto não concede acesso que ele não conceda.
          </p>

          {avisoInicial !== null && <Aviso>{avisoInicial}</Aviso>}
          {erro !== null && (
            <Aviso tipo="erro">
              {erro}
              {dica !== null && <div className="dica">{dica}</div>}
            </Aviso>
          )}
          {/* Fora do bloco de erro e depois dele: não é o motivo da recusa, é a
              consequência da próxima — e precisa sobreviver a um `erro` novo. */}
          {avisoBloqueio !== null && <Aviso tipo="alerta">{avisoBloqueio}</Aviso>}

          <form onSubmit={(e) => void enviar(e)}>
            <Campo rotulo="Usuário do KACE">
              <input
                name="usuario"
                autoComplete="username"
                placeholder="nome@empresa.com.br"
                defaultValue={ultimo?.usuario ?? ''}
                // Se já sabemos o usuário, o cursor vai direto para a senha.
                autoFocus={ultimo === null}
              />
            </Campo>

            <Campo rotulo="Senha">
              <span className="com-botao">
                <input
                  name="senha"
                  type={verSenha ? 'text' : 'password'}
                  autoComplete="current-password"
                  onKeyUp={conferirCapsLock}
                  onKeyDown={conferirCapsLock}
                  autoFocus={ultimo !== null}
                />
                <button
                  type="button"
                  className="revelar"
                  onClick={() => setVerSenha((v) => !v)}
                  // `title` e `aria-label` diferentes de propósito: o primeiro é
                  // dica de mouse, o segundo é o que o leitor de tela anuncia.
                  title={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-pressed={verSenha}
                  tabIndex={-1}
                >
                  {verSenha ? '🙈' : '👁'}
                </button>
              </span>
            </Campo>
            {capsLock && <p className="aviso-inline">⇪ Caps Lock está ligado.</p>}

            {/*
              🔴 **Campo aberto com sugestões, nunca uma lista fechada.**

              Era um `<select>` quando havia organizações conhecidas. A lista
              vem da última configuração gravada, e ela envelhece: medido no
              cliente, a organização foi renomeada de "3. Mafaldo's LAB" para
              "2. LenanzoTech" e a tela passou a oferecer **só a antiga**.

              O usuário ficou trancado do lado de fora pela nossa própria
              interface — com a credencial certa, o appliance no ar, e nenhum
              jeito de digitar o nome novo. Uma lista fechada sobre um dado que
              vive no appliance é uma promessa que o appliance não assinou.

              `datalist` sugere sem restringir: quem tem a lista certa clica,
              quem precisa de outra digita.
            */}
            <Campo
              rotulo="Organização"
              ajuda={
                organizacoes.length > 0
                  ? 'Escolha ou digite. Se a organização foi renomeada no KACE, o nome novo vale — digite-o aqui.'
                  : 'Exatamente como aparece no KACE, com o prefixo numérico. Em branco usa a configurada na instalação.'
              }
            >
              <input
                name="organizacao"
                defaultValue={organizacaoPadrao}
                list="organizacoes-conhecidas"
                autoComplete="off"
                spellCheck={false}
              />
              {organizacoes.length > 0 && (
                <datalist id="organizacoes-conhecidas">
                  {organizacoes.map((o) => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
              )}
            </Campo>

            <label className="lembrar">
              <input
                type="checkbox"
                checked={lembrar}
                onChange={(e) => setLembrar(e.target.checked)}
              />
              <span>
                Lembrar meu usuário
                <em>A senha nunca é guardada aqui — isso é trabalho do navegador.</em>
              </span>
            </label>

            <button type="submit" className="primario largo" disabled={entrando}>
              {entrando ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          {/*
            🔴 Entrada local de teste — só aparece quando o servidor foi ligado
            com ACESSO_LOCAL no ambiente.

            ⚠️ O bloco é feio de propósito: fundo de alerta, aviso explícito de
            que não é login do KACE. Um atalho de autenticação que parece um
            recurso normal é como ele acaba em produção.
          */}
          {acessoLocalDisponivel && (
            <div className="acesso-local">
              <Aviso tipo="alerta">
                <strong>Entrada local de teste habilitada.</strong> Isto não é um
                login do KACE — serve para navegar pelo produto com os dados já
                sincronizados quando o appliance está indisponível. Só funciona
                desta máquina.
              </Aviso>
              <div className="linha">
                <input
                  type="password"
                  value={segredoLocal}
                  placeholder="Segredo do ACESSO_LOCAL"
                  onChange={(e) => setSegredoLocal(e.target.value)}
                />
                <button
                  type="button"
                  className="encolhe"
                  disabled={entrando || segredoLocal === ''}
                  onClick={() => {
                    setEntrando(true);
                    setErro(null);
                    void api
                      .post<RespostaLogin>('/api/auth/local', { segredo: segredoLocal })
                      .then((r) => aoEntrar(r.usuario, r.capacidades))
                      .catch((e: unknown) =>
                        setErro(e instanceof Error ? e.message : 'Segredo inválido.'),
                      )
                      .finally(() => setEntrando(false));
                  }}
                >
                  Entrar sem o KACE
                </button>
              </div>
            </div>
          )}

          {/*
            Duas limitações reais, ditas antes de virarem chamado de suporte.
            Conta com 2FA é rejeitada pela API do KACE, e conta com escopo de
            dispositivos restrito é recusada por decisão nossa.
          */}
          <details className="ajuda-entrada">
            <summary>Não consigo entrar</summary>
            <ul>
              <li>
                <strong>Conta com dois fatores não entra.</strong> A API do KACE rejeita
                autenticação de conta com 2FA, e não há como contornar pelo produto.
              </li>
              <li>
                <strong>Conta com visibilidade parcial do parque é recusada.</strong> Se
                o console só te mostra parte dos dispositivos, o produto não te mostra o
                resto — seria conceder mais acesso do que o KACE concede.
              </li>
              <li>
                <strong>Esqueceu a senha?</strong> A troca é no KACE. O produto não guarda
                senha de ninguém.
              </li>
            </ul>
          </details>
        </div>
      </main>
    </div>
  );
}
